import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2Icon, HelpCircleIcon } from "lucide-react";
import type { OnboardingAnswers, QuizItem } from "../../../types/onboarding";
import { StepHeading } from "../StepHeading";
import { ChoiceTile } from "../../games/ChoiceTile";
import { quizFinished } from "../../../utils/placement";
import { spring } from "../../../utils/motion";
interface QuizStepProps {
  items: QuizItem[];
  answers: OnboardingAnswers;
  update: (p: Partial<OnboardingAnswers>) => void;
  onDone: () => void;
  targetLang: string;
}

/**
 * Adaptive quick check: questions get harder and we stop after two misses.
 * Deliberately no right/wrong reveal — it's a placement, not a test.
 */
export function QuizStep({
  items,
  answers,
  update,
  onDone,
  targetLang
}: QuizStepProps) {
  const reduce = useReducedMotion() ?? false;
  const [picked, setPicked] = useState<number | null>(null);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const done = quizFinished(answers.quiz, items.length);
  const current = items[answers.quiz.length];
  const record = (result: boolean | null, option: number | null) => {
    setPicked(option);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setPicked(null);
      update({
        quiz: [...answers.quiz, result]
      });
    }, 240);
  };
  return <div data-id="element-1044" data-mp="2fi3u26272e94">
      <StepHeading title="A quick check" description="Up to five short questions, getting harder. Not sure? Say so — guessing only puts you in the wrong place." data-id="element-1045" data-mp="2fhu5mmwssf5j" />

      <div className="mb-5 flex items-center gap-1.5" aria-hidden="true" data-id="element-1046" data-mp="26ll514x33aht">
        {items.map((q, k) => <span key={q.id} className={`h-2 flex-1 rounded-full ${k < answers.quiz.length ? "bg-brand/60" : k === answers.quiz.length && !done ? "bg-brand/25" : "bg-sunken"}`} data-id="element-1047" data-mp="21mrot4qex5dy" data-mpi={k} />)}
      </div>

      <>
        {done || !current ? <motion.div key="done" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={spring.smooth} className="rounded-panel border-3 border-line bg-mint p-7 text-center shadow-brutal-sm" role="status" data-id="element-1048" data-mp="1i2jd08j0wpi9">
            <CheckCircle2Icon className="mx-auto h-9 w-9 text-good" strokeWidth={2} aria-hidden="true" data-id="element-1049" data-mp="1d5smssdi8dj4" />
            <p className="mt-3 text-xl font-bold text-ink" data-id="element-1050" data-mp="20vdhu9mxn18r">That's all we need.</p>
            <p className="mt-1 text-[15px] text-ink-2" data-id="element-1051" data-mp="25rlthd1x8cb6">
              {answers.quiz.length < items.length ? "We stopped early — no point making it harder than it has to be." : "You went all the way. Nice."}
            </p>
            <button type="button" onClick={onDone} className="focus-ring mt-5 rounded-lg text-sm font-semibold text-ink underline underline-offset-4" data-id="element-1052" data-mp="1w07owqv44mb6">
              See my starting point
            </button>
          </motion.div> : <motion.div key={current.id} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 28
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: -28,
        transition: {
          duration: 0.15
        }
      }} transition={spring.smooth} data-id="element-1053" data-mp="d34g7sc0dut3">
            <p className="text-sm font-bold text-ink-2" data-id="element-1054" data-mp="hvxgo40g2gzd">{current.prompt}</p>
            {current.sentence && <p className="mt-2 rounded-card border-3 border-line bg-surface px-5 py-4 text-2xl font-semibold text-ink md:text-[28px]" lang={targetLang} data-id="element-1055" data-mp="3qao8mhkxe23o">
                {current.sentence.split("___").map((part, k, arr) => <React.Fragment key={k} data-id="element-1056" data-mp="3v9ip3cvf7ekm" data-mpi={k}>
                    {part}
                    {k < arr.length - 1 && <span className="mx-1 inline-block w-16 border-b-3 border-dashed border-line/50 align-middle" aria-label="blank" data-id="element-1057" data-mp="3vbveuc7f6t88" />}
                  </React.Fragment>)}
              </p>}
            <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-1058" data-mp="3g7u1ikr4us05">
              {current.options.map((o, k) => <li key={o} data-id="element-1059" data-mp="3l65ayudin0hu" data-mpi={k}>
                  <ChoiceTile state={picked === k ? "selected" : "idle"} disabled={picked !== null} onClick={() => record(k === current.answer, k)} className="w-full" lang={current.sentence ? targetLang : undefined} data-id="element-1060" data-mp="3l7gdyrz5047w">
                    {o}
                  </ChoiceTile>
                </li>)}
            </ul>
            <button type="button" disabled={picked !== null} onClick={() => record(null, null)} className="focus-ring mt-4 flex items-center gap-1.5 rounded-lg text-sm font-semibold text-ink-2 hover:text-ink" data-id="element-1061" data-mp="cqty3x86f5iq">
              <HelpCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-1062" data-mp="lpfd5iefw2er" /> I'm not sure
            </button>
          </motion.div>}
      </>
    </div>;
}